import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { reduxForm, SubmissionError } from 'redux-form';
import StandAloneLayout from '../layouts/StandAloneLayout/StandAloneLayout';
import {
  login,
  logout,
  sendTwoFactorAuthCode,
  AUTH_ERROR,
  AUTH_NONE,
  AUTH_SUCCESS,
  ENABLE_2FA_REQUIRED,
  setUserNotAuthenticated,
} from '@t7x000/apex-web/lib/redux/actions/authActions.js';
import TwoFactorAuthModal from '@t7x000/apex-web/lib/components/2FA/TwoFactorAuthComponent';
import path from '@t7x000/apex-web/lib/helpers/path';
import { showSnack } from '@t7x000/apex-web/lib/redux/actions/snackbarActions';
import { clearLoginTradeBypass, setLoginTradeBypass } from '../helpers/loginTradeBypass';

const FORM_ID = 'login-trade';
const ENFORCE_2FA_MESSAGE =
  'System enforces to enable 2FA on this account. Enable 2FA on this account to be able to login';

const LoginTrade = ({
  handleLogin,
  finishLogin,
  closeTwoFactor,
  isConnected,
  isAuthenticated,
  is2FARequired,
  logoutUser,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [hadSessionOnArrival] = useState(() => isAuthenticated);
  const [isTwoFactorOpen, setIsTwoFactorOpen] = useState(false);
  const sessionClearedRef = useRef(false);
  const jwtRef = useRef('');

  useEffect(() => {
    if (hadSessionOnArrival && !sessionClearedRef.current) {
      sessionClearedRef.current = true;
      clearLoginTradeBypass();
      logoutUser();
    }
  }, [hadSessionOnArrival, logoutUser]);

  /**
   * Hook opens 2FA modal.
   * Context: by default is2FARequired is false and it becomes true when user submits
   * login form and API responds that account has 2FA enabled. Inner logic of "login"
   * action (apex-web) sets is2FARequired to true, this component catches it and opens 2FA modal.
   */
  useEffect(() => {
    if (is2FARequired) {
      setIsTwoFactorOpen(true);
    }
  }, [is2FARequired]);

  const canSubmit = useMemo(() => {
    return !!isConnected && !!username && !!password && !isSubmitting;
  }, [isConnected, username, password, isSubmitting]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSubmit) return;

      setErrorText('');
      setIsSubmitting(true);
      const JWTToken = generateRandomToken();
      jwtRef.current = JWTToken;
      try {
        await handleLogin({
          username,
          password,
          JWTToken,
          setErrorText,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [canSubmit, handleLogin, username, password],
  );

  const onTwoFactorSuccess = useCallback(() => {
    setIsTwoFactorOpen(false);
    if (jwtRef.current) {
      finishLogin(jwtRef.current);
    }
  }, [finishLogin]);

  const onCloseTwoFactor = useCallback(() => {
    setIsTwoFactorOpen(false);
    closeTwoFactor();
  }, [closeTwoFactor]);

  return (
    <StandAloneLayout>
      <div
        style={{
          width: 320,
          margin: 'auto auto',
          padding: 24,
          borderRadius: 8,
          border: '1px solid #ccc',
        }}>
        <h2 style={{ margin: '0 0 16px 0' }}>Login Trade</h2>
        <form id={FORM_ID} onSubmit={onSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              style={{ width: '100%', padding: 10 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              style={{ width: '100%', padding: 10 }}
            />
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: 12,
              backgroundColor: '#0171b6',
              cursor: 'pointer',
              color: 'white',
            }}>
            {isSubmitting ? 'Logging in…' : 'Log In'}
          </button>
          {!!errorText && <div style={{ marginTop: 12, color: '#b00020' }}>{errorText}</div>}
        </form>
        <LoginTradeTwoFactorModal isOpen={isTwoFactorOpen} close={onCloseTwoFactor} onSuccess={onTwoFactorSuccess} />
      </div>
    </StandAloneLayout>
  );
};

const mapStateToProps = (state) => ({
  isConnected: state.wsConnection.isConnected,
  isAuthenticated: state.auth.isAuthenticated,
  is2FARequired: state.auth.is2FARequired,
});

const resetAuthState = (dispatch) => {
  dispatch(logout());
  dispatch(setUserNotAuthenticated(false));
  dispatch({
    type: AUTH_NONE,
    payload: {
      is2FARequired: false,
    },
  });
};

const showLoginError = (dispatch, setErrorText, msg) => {
  if (typeof setErrorText === 'function') {
    setErrorText(msg);
  }
  dispatch(
    showSnack({
      id: 'loginTradeAuthError',
      text: msg,
      type: 'warning',
    }),
  );
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const finishLogin = (jwtToken) => {
    localStorage.setItem('JWTToken', jwtToken);
    setLoginTradeBypass();
    ownProps.history.replace(path(process.env.REACT_APP_DEFAULT_PATH));
  };
  return {
    logoutUser: () => dispatch(logout()),
    finishLogin,
    closeTwoFactor: () => {
      resetAuthState(dispatch);
    },
    handleLogin: async (payload) => {
      const loginData = {
        username: payload.username,
        password: payload.password,
      };
      return dispatch(login(loginData)).then((action) => {
        if (action.type === AUTH_ERROR) {
          showLoginError(
            dispatch,
            payload?.setErrorText,
            action?.payload?.errorMsg || payload?.errorMsg || 'Invalid username or password',
          );
        } else if (action.type === ENABLE_2FA_REQUIRED) {
          showLoginError(dispatch, payload?.setErrorText, ENFORCE_2FA_MESSAGE);
          resetAuthState(dispatch);
        } else if (action.type === AUTH_SUCCESS) {
          finishLogin(payload.JWTToken);
        }

        // This code is reached in the following cases:
        // 1. Unexpected action type returned by login action
        // 2. 2FA is enabled for the account. It means 2FA case is none of if/else statements
        //    above. In this case we simply return the action
        //    why: internal logic in "login" action has already updated store state
        //    and 2FA modal is shown
        return action;
      });
    },
  };
};

const LoginTradeContainer = connect(mapStateToProps, mapDispatchToProps)(LoginTrade);

export default withRouter(LoginTradeContainer);

function generateRandomToken() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/=+$/g, '');
}

const LoginTradeTwoFactorModal = reduxForm({
  form: 'loginTradeTwoFactorAuth',
  onSubmit: async (formValues, dispatch, ownProps) => {
    const action = await dispatch(sendTwoFactorAuthCode(formValues.code));
    if (!action || action.type === AUTH_ERROR) {
      throw new SubmissionError({
        _error: action?.payload?.errorMsg || 'Invalid Auth Code. Please try again.',
      });
    }
    if (action.type === AUTH_SUCCESS && typeof ownProps.onSuccess === 'function') {
      ownProps.onSuccess();
    }
    return action;
  },
  onChange: (values, _dispatch, props, previousValues) => {
    const code = values?.code || '';
    const previousCode = previousValues?.code || '';
    if (/^\d{6}$/.test(code) && code !== previousCode && !props.submitting) {
      props.submit().catch(() => undefined);
    }
  },
})(TwoFactorAuthModal);
